import { Backdrop, CircularProgress, Typography } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import InsertTemplateModal from "../components/InsertTemplateModal";
import { PreviewContextProvider } from "../components/PreviewContext";
import ResizeHandle from "../components/ResizeHandle";
import { SearchContextProvider } from "../components/SearchContext";
import SiteBuilderBar from "../components/SiteBuilderBar";
import InteractiveMapEditor from "../components/tree/InteractiveMapEditor";
import JsonEditor from "../components/tree/JsonEditor";
import { CustomizerContext } from "../context/CustomizerContext";
import { get } from "../helpers/request";
import { useYObject } from "../hooks/documentHooks";
import CustomizationTabs from "./CustomizationTabs";
import PreviewContainer from "./PreviewContainer";
import {
  EditorContainer,
  MainContainer,
  StyledDrawer,
} from "./SiteBuilderElements";

const SiteBuilder = () => {
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const { document, documentReady } = useContext(CustomizerContext);
  const settingsMap = documentReady && document && document.getMap("data");
  const metaMap = documentReady && settingsMap && settingsMap.get("meta")
  const meta = useYObject(metaMap)

  const handleChangeVersion = useCallback(({ target }) => {
    const newVersion = target.value
    if (!newVersion) return
    if (metaMap) {
      metaMap.set("engine_version", newVersion)
    }
  }, [metaMap])

  useEffect(() => {
    if (meta?.engine_version && versions) {
      const foundVersion = versions.find(version => version.template_version_id === meta.engine_version)
      if (foundVersion) {
        setSelectedVersion(foundVersion)
      } else {
        setSelectedVersion({
          description: 'Master Template',
          template_version_id: 'master'
        })
      }        
    }
  }, [meta, versions])

  useEffect(() => {
    get(`${process.env.REACT_APP_API_URL}/internal/admin/v3/templates/versions`)
      .then((data) => setVersions(data?.templates))
      .catch((error) => console.error(error));
  }, []);

  const siteRouteElement = useMemo(() => {
    return (
      <SiteBuilderContainer
        handleChangeVersion={handleChangeVersion}
        versions={versions}
        selectedVersion={selectedVersion}
      />      
    )
  }, [handleChangeVersion, versions, selectedVersion])

  const defaultRouteElement = useMemo(() => <EnsureSwitchSiteModal />, [])

  return (
    <EditorContainer>
      <SearchContextProvider scope={settingsMap}>
        <PreviewContextProvider>
          <Routes>
            <Route path="/:site_id" element={siteRouteElement} />
            <Route path='*' element={defaultRouteElement} />
          </Routes>          
          <JsonEditor />
          <InteractiveMapEditor />
        </PreviewContextProvider>
        <InsertTemplateModal />
      </SearchContextProvider>
    </EditorContainer>
  );
};

const loaderStyle = { 
  color: '#fff',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column'
}

const SiteBuilderContainer = ({ handleChangeVersion, versions, selectedVersion }) => {
  const { documentReady } = useContext(CustomizerContext);
  const drawerRef = useRef();

  return (
    <>
      <SiteBuilderBar selectedVersion={selectedVersion} />
      <MainContainer>
        {documentReady &&
          <>
            <StyledDrawer ref={drawerRef}>
              <CustomizationTabs />
            </StyledDrawer>
            <ResizeHandle element={drawerRef} />
            <PreviewContainer
              handleChangeVersion={handleChangeVersion}
              versions={versions}
              selectedVersion={selectedVersion}
            />
          </>
        }
      </MainContainer>
      {!documentReady &&
        <Backdrop sx={loaderStyle} open>
          <CircularProgress />
          <Typography variant="subtitle1">Cargando...</Typography>
        </Backdrop>      
      }
    </>
  )
}

const EnsureSwitchSiteModal = () => {
  const open = useSelector(state => state.switchSiteModalOpen)
  const dispatch = useDispatch();
  
  useEffect(() => {
    if (!open) dispatch({ type: 'show-switch-site', open: true })
  }, [open, dispatch])

  return null
}

export default SiteBuilder;
